import { useEffect, useState, lazy, Suspense } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import HomePage from '../pages/HomePage';
import { useSubdomain } from '../hooks/useSubdomain';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { isMenuMode } from '../utils/menuCategories';
import { Capacitor } from '@capacitor/core';

// Lazy load para evitar cargar estas páginas grandes en el bundle principal
const StorePage = lazy(() => import('../pages/StorePage'));
const MenuPage = lazy(() => import('../pages/MenuPage'));

// Loader componente reutilizable (fuera del componente para evitar re-creación)
const Loader = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
    </div>
);

// Componente que redirige a /dashboard si el usuario está autenticado,
// o muestra la HomePage si no lo está
export default function RootRedirect() {
    const { user, loading } = useAuth();
    const [searchParams] = useSearchParams();
    const subdomain = useSubdomain();
    const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
    const [businessCategory, setBusinessCategory] = useState<string | null>(null);

    // ============================================================
    // TODOS LOS useEffect DEBEN estar ANTES de cualquier return
    // (Regla de Hooks de React: siempre en el mismo orden)
    // ============================================================

    // 1) Detectar si es una recuperación de contraseña que llega a la raíz
    useEffect(() => {
        const recovery = searchParams.get('recovery');
        if (recovery === 'true') {
            const params = new URLSearchParams(window.location.search);
            window.location.href = `/login?${params.toString()}`;
        }
    }, [searchParams]);

    // 2) Verificar permisos del subdominio si existe
    useEffect(() => {
        if (!subdomain) return;

        const checkSubdomainPermission = async () => {
            try {
                const { data, error } = await supabase
                    .from('business_profiles')
                    .select('subscription_plan, subdomain_enabled, business_category')
                    .eq('slug', subdomain)
                    .maybeSingle();

                if (error || !data) {
                    setIsAuthorized(false);
                    return;
                }

                const hasPermission =
                    data.subscription_plan === 'enterprise' ||
                    data.subdomain_enabled === true;

                if (hasPermission) {
                    setBusinessCategory(data.business_category);
                    setIsAuthorized(true);
                } else {
                    toast.error('Esta tienda no tiene habilitado el acceso por subdominio.');
                    setIsAuthorized(false);
                }
            } catch (err) {
                console.error('Error checking subdomain:', err);
                setIsAuthorized(false);
            }
        };

        checkSubdomainPermission();
    }, [subdomain]);

    // ============================================================
    // RENDER - Lógica simple y sin estados asíncronos pendientes
    // ============================================================

    // Si es recovery, el useEffect ya lo redirigió
    if (searchParams.get('recovery') === 'true') {
        return <Loader />;
    }

    // Mientras está cargando la autenticación, mostrar loader
    if (loading) {
        return <Loader />;
    }

    // PRIORIDAD 1: Si estamos en un subdominio → mostrar la tienda
    if (subdomain) {
        if (isAuthorized === null) return <Loader />;

        if (isAuthorized) {
            return (
                <Suspense fallback={<Loader />}>
                    {isMenuMode(businessCategory || undefined) ? <MenuPage /> : <StorePage />}
                </Suspense>
            );
        }

        // No autorizado → mandar a store-not-found
        return <Navigate to="/store-not-found" replace />;
    }

    // PRIORIDAD 2: Usuario logueado → redirigir a /dashboard INMEDIATAMENTE
    // La lógica de rol específico (waiter → /pos/tables, etc.) se maneja en el Dashboard/Layout
    // Esto evita quedarse en un loader infinito esperando una query asíncrona
    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    // PRIORIDAD 3: Visitante anónimo en la raíz
    // Si es la aplicación nativa móvil, enviarlo directo al Login
    if (Capacitor.isNativePlatform()) {
        return <Navigate to="/login" replace />;
    }

    // Si es en la web, mostrar la Landing Page (HomePage)
    return <HomePage />;
}
